import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { postTest } from "../../../../services/TestService";
import { useExamAnswers } from "../../../hooks/useExamAnswers";

// Import các component mới
import {
  ExamHeader,
  ExamNavigation,
  ExamInfoPanel,
  ExamFixedInfoPanel,
  ExamQuestion,
  ExamModals,
  TimeUpModal,
} from "./components";
import SubmitConfirmationModal from "./components/SubmitConfirmationModal";

import { isNumeric } from "../../../../common/Utils";
import { LoadingSpinner } from "../../../common";
import { getExamById } from "../../../../services/ExamService";

// Tạo storage key dựa trên examId và userId
const getStorageKey = (examId) => {
  return `exam_answers_${examId}`;
};

// Tạo storage key cho thời gian
const getTimeStorageKey = (examId) => {
  return `exam_time_${examId}`;
};

// Tạo storage key cho câu hỏi đánh dấu
const getMarkedQuestionsStorageKey = (examId) => {
  return `exam_marked_questions_${examId}`;
};

// Thêm hàm lưu và lấy current index vào localStorage
const getCurrentQuestionKey = (examId) => `exam_current_${examId}`;

const saveCurrentToStorage = (examId, current) => {
  try {
    localStorage.setItem(getCurrentQuestionKey(examId), current.toString());
  } catch (e) {}
};
const getCurrentFromStorage = (examId) => {
  try {
    const val = localStorage.getItem(getCurrentQuestionKey(examId));
    if (val !== null) return parseInt(val, 10);
    return 0;
  } catch (e) {
    return 0;
  }
};

// Lưu đáp án vào localStorage
const saveAnswersToStorage = (examId, answers) => {
  try {
    const storageKey = getStorageKey(examId);
    const answersLocal = getAnswersFromStorage(examId);
    if (answersLocal) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ ...answersLocal, ...answers })
      );
    } else {
      localStorage.setItem(storageKey, JSON.stringify(answers));
    }
  } catch (error) {
    console.error("Error saving answers to localStorage:", error);
  }
};

// Lưu câu hỏi đánh dấu vào localStorage
const saveMarkedQuestionsToStorage = (examId, markedQuestions) => {
  try {
    const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(examId);
    localStorage.setItem(
      markedQuestionsStorageKey,
      JSON.stringify(markedQuestions)
    );
  } catch (error) {
    console.error("Error saving marked questions to localStorage:", error);
  }
};

// Lấy đáp án từ localStorage
const getAnswersFromStorage = (examId) => {
  try {
    const storageKey = getStorageKey(examId);
    const savedAnswers = localStorage.getItem(storageKey);
    if (savedAnswers) {
      return JSON.parse(savedAnswers);
    }
    return null;
  } catch (error) {
    console.error("Error getting answers from localStorage:", error);
    return null;
  }
};

// Lấy thời gian từ localStorage
const getTimeFromStorage = (examId) => {
  try {
    const timeStorageKey = getTimeStorageKey(examId);
    const savedTime = localStorage.getItem(timeStorageKey);
    if (savedTime) {
      return parseInt(savedTime, 10);
    }
    return null;
  } catch (error) {
    console.error("Error getting time from localStorage:", error);
    return null;
  }
};

// Lấy câu hỏi đánh dấu từ localStorage
const getMarkedQuestionsFromStorage = (examId) => {
  try {
    const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(examId);
    const savedMarkedQuestions = localStorage.getItem(
      markedQuestionsStorageKey
    );
    if (savedMarkedQuestions) {
      return JSON.parse(savedMarkedQuestions);
    }
    return [];
  } catch (error) {
    console.error("Error getting marked questions from localStorage:", error);
    return [];
  }
};

function formatTime(sec) {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const ExamTestPage = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [examData, setExamData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsMQ, setQuestionsMQ] = useState([]);
  const [initialAnswers, setInitialAnswers] = useState({});

  // Use centralized answer management
  const { answers, updateAnswer, clearAnswers } = useExamAnswers(
    id,
    initialAnswers
  );
  // Mảng lưu trữ các câu hỏi được đánh dấu để làm sau
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [currentMQ, setCurrentMQ] = useState(0);
  const [current, setCurrent] = useState(() => getCurrentFromStorage(id));
  // Logic disabled cho navigation buttons - Smart navigation calculation
  const calculateNavigationDisabled = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return { isDisabledNext: true, isDisabledBack: true };

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Lấy tất cả question numbers có sẵn và sort
    const allAvailableQuestionNumbers = questions
      .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
      .filter((num) => !isNaN(num))
      .sort((a, b) => a - b);

    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Check if current question is in MQ group
    const currentMQGroup = sortedMQGroups.find((group) =>
      group.range.includes(currentQuestionNumber)
    );

    // Calculate NEXT
    let hasNext = false;
    if (currentMQGroup) {
      // Trong MQ group - tìm MQ group tiếp theo
      const currentMQIndex = sortedMQGroups.findIndex(
        (group) => group === currentMQGroup
      );
      const nextMQGroup = sortedMQGroups[currentMQIndex + 1];

      if (nextMQGroup) {
        hasNext = true;
      } else {
        // Không có MQ group tiếp theo - check single questions sau range này
        const lastQuestionInCurrentRange = Math.max(...currentMQGroup.range);
        const nextQuestionNumbers = allAvailableQuestionNumbers.filter(
          (num) =>
            num > lastQuestionInCurrentRange &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );
        hasNext = nextQuestionNumbers.length > 0;
      }
    } else {
      // Single question - tìm target tiếp theo (MQ group hoặc single)
      const nextQuestionNumbers = allAvailableQuestionNumbers.filter(
        (num) => num > currentQuestionNumber
      );
      hasNext = nextQuestionNumbers.length > 0;
    }

    // Calculate BACK
    let hasBack = false;
    if (currentMQGroup) {
      // Trong MQ group - tìm MQ group trước đó
      const currentMQIndex = sortedMQGroups.findIndex(
        (group) => group === currentMQGroup
      );
      const prevMQGroup = sortedMQGroups[currentMQIndex - 1];

      if (prevMQGroup) {
        hasBack = true;
      } else {
        // Không có MQ group trước đó - check single questions trước range này
        const firstQuestionInCurrentRange = Math.min(...currentMQGroup.range);
        const prevQuestionNumbers = allAvailableQuestionNumbers.filter(
          (num) =>
            num < firstQuestionInCurrentRange &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );
        hasBack = prevQuestionNumbers.length > 0;
      }
    } else {
      // Single question - tìm target trước đó (MQ group hoặc single)
      const prevQuestionNumbers = allAvailableQuestionNumbers.filter(
        (num) => num < currentQuestionNumber
      );
      hasBack = prevQuestionNumbers.length > 0;
    }

    return {
      isDisabledNext: !hasNext,
      isDisabledBack: !hasBack,
    };
  };

  const { isDisabledNext, isDisabledBack } = calculateNavigationDisabled();
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [shouldPromptFullscreen, setShouldPromptFullscreen] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const exitFullscreenTimerRef = useRef(null);

  const handleFetch = async () => {
    try {
      setLoading(true);
      const responseExam = await getExamById(id);
      const examData = responseExam?.data;
      if (examData) {
        setExamData(examData);
        setQuestions(examData?.questions.filter((q) => q.type !== "MQ"));
        setQuestionsMQ(examData?.questions.filter((q) => q.type === "MQ"));

        // Lấy danh sách câu hỏi đã đánh dấu từ localStorage
        const savedMarkedQuestions = getMarkedQuestionsFromStorage(id);
        setMarkedQuestions(savedMarkedQuestions);

        // Initialize answers object based on question types
        const initialAnswersObj = {};
        examData?.questions?.forEach((q) => {
          if (!q.question) {
            console.error("Question missing question property:", q);
            return;
          }

          switch (q.type) {
            case "TN":
              initialAnswersObj[q.question] = null;
              break;
            case "MA":
              initialAnswersObj[q.question] = [];
              break;
            case "DS":
              initialAnswersObj[q.question] = {};
              break;
            case "TLN":
              initialAnswersObj[q.question] = "";
              break;
            case "TLN_M":
              initialAnswersObj[q.question] = {
                "1.": "",
                "2.": "",
                "3.": "",
                "4.": "",
                "5.": "",
              };
              break;
            case "KT":
              initialAnswersObj[q.question] = {
                slot1: null,
                slot2: null,
                slot3: null,
                slot4: null,
                slot5: null,
                slot6: null,
                slot7: null,
                slot8: null,
                slot9: null,
                slot10: null,
              };
              break;
            default:
              if (q.type !== "MQ") {
                initialAnswersObj[q.question] = null;
              }
          }
        });

        setInitialAnswers(initialAnswersObj);
      }
    } catch (error) {
      console.log("handleFetch ~ error:", error);
      toast.error("Cần nâng cấp tài khoản để xem đề thi");
      navigate(`/practice-room`);
    } finally {
      setLoading(false);
    }
  };

  // Hàm cập nhật currentMQ dựa trên câu hỏi hiện tại
  const updateCurrentMQFromCurrentQuestion = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Kiểm tra xem câu hỏi có thuộc MQ group không
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);
    const mqGroup = sortedMQGroups.find((e) =>
      e?.range.includes(currentQuestionNumber)
    );

    if (mqGroup) {
      // Nếu thuộc MQ group, set currentMQ = question number
      setCurrentMQ(currentQuestionNumber);
    } else {
      // Nếu không thuộc MQ group (câu hỏi bình thường), set currentMQ = 0
      setCurrentMQ(0);
    }
  };

  useEffect(() => {
    handleFetch();
    // Khi examId đổi, reset current về giá trị đã lưu (nếu có)
    const savedCurrent = getCurrentFromStorage(id);
    setCurrent(savedCurrent);
  }, [id]);

  // Cập nhật currentMQ khi current hoặc questions thay đổi
  useEffect(() => {
    if (questions.length > 0) {
      updateCurrentMQFromCurrentQuestion();
    }
  }, [current, questions, questionsMQ]);

  // Remove manual save logic - useExamAnswers handles this automatically

  // Lưu current vào localStorage mỗi khi current thay đổi
  useEffect(() => {
    saveCurrentToStorage(id, current);
  }, [current, id]);

  // Hàm tự động submit khi thoát fullscreen 10 giây (được định nghĩa trước để dùng trong useEffect)
  const handleAutoSubmitOnExitFullscreen = useCallback(async () => {
    if (isSubmitting || isTimeUp) return;

    try {
      setIsSubmitting(true);
      const answersForSubmit = {};
      let savedAnswers = getAnswersFromStorage(id);
      Object.keys(savedAnswers || {}).map((key) => {
        const question = questions.find((q) => q.question === key);
        answersForSubmit[key] = {
          answer: savedAnswers[key],
          type: question?.type,
        };
      });

      const res = await postTest(id, {
        userAnswers: answersForSubmit || {},
        examId: id,
        access: examData?.access,
      });

      toast.success(res.message || "Đã tự động nộp bài thi");

      // Clear answers using hook
      clearAnswers();

      const timeStorageKey = getTimeStorageKey(id);
      const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(id);
      localStorage.removeItem(timeStorageKey);
      localStorage.removeItem(markedQuestionsStorageKey);
      localStorage.removeItem(`exam_time_${id}`);
      localStorage.removeItem(`current_time_${id}_${current}`);
      localStorage.removeItem(`exam_current_${id}`);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key.startsWith("current_time_")) {
          localStorage.removeItem(key);
        }
      }

      // Tự động thoát toàn màn hình khi nộp bài thành công
      exitFullscreen();

      navigate(`/exam-result/${id}`);
    } catch (error) {
      console.error("Auto submit on exit fullscreen failed:", error);
      const message = error?.response?.data?.message || error.message;
      toast.error(message || "Lỗi khi tự động nộp bài. Vui lòng thử lại.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    id,
    questions,
    examData,
    current,
    clearAnswers,
    navigate,
    isSubmitting,
    isTimeUp,
  ]);

  // useEffect KHÔNG gọi requestFullscreen trực tiếp nữa
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      if (!isFullscreen) {
        // User đã thoát khỏi fullscreen
        setShowFullscreenModal(true);

        // Clear timer cũ nếu có
        if (exitFullscreenTimerRef.current) {
          clearTimeout(exitFullscreenTimerRef.current);
        }

        // Đếm 10 giây và tự động submit nếu không quay lại fullscreen
        exitFullscreenTimerRef.current = setTimeout(() => {
          // Kiểm tra lại xem có đang fullscreen không
          const stillNotFullscreen =
            !document.fullscreenElement &&
            !document.webkitFullscreenElement &&
            !document.mozFullScreenElement &&
            !document.msFullscreenElement;

          if (stillNotFullscreen) {
            // Tự động submit bài thi
            console.log("Tự động submit bài thi sau 10 giây thoát fullscreen");
            handleAutoSubmitOnExitFullscreen();
          }
        }, 10000); // 10 giây
      } else {
        // User đã quay lại fullscreen
        setShowFullscreenModal(false);

        // Hủy timer nếu user quay lại fullscreen trong vòng 10 giây
        if (exitFullscreenTimerRef.current) {
          clearTimeout(exitFullscreenTimerRef.current);
          exitFullscreenTimerRef.current = null;
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );

      // Cleanup timer khi component unmount
      if (exitFullscreenTimerRef.current) {
        clearTimeout(exitFullscreenTimerRef.current);
        exitFullscreenTimerRef.current = null;
      }
    };
  }, [handleAutoSubmitOnExitFullscreen]);

  const handleBackToFullscreen = () => {
    setShowFullscreenModal(false);
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  // Hàm thoát toàn màn hình
  const exitFullscreen = () => {
    try {
      if (
        document.visibilityState === "visible" &&
        document.fullscreenElement
      ) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.warn("exitFullscreen error:", error);
    }
  };

  // Helper function để check xem câu hỏi hiện tại có thuộc MQ range không
  const isCurrentQuestionMQ = () => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return false;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    return sortedMQGroups.some((group) =>
      group.range.includes(currentQuestionNumber)
    );
  };

  useEffect(() => {
    const handleKeyEvent = (e) => {
      // Print Screen (keyCode 44)
      if (e.keyCode === 44) {
        handleScreenshotAttempt();
        return;
      }

      // Alt + Print Screen
      if (e.altKey && e.keyCode === 44) {
        handleScreenshotAttempt();
        return;
      }

      // Windows + Shift + S (Snipping Tool)
      if (e.metaKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        handleScreenshotAttempt();
        return;
      }

      // Ctrl + Shift + I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        handleScreenshotAttempt();
        return;
      }
    };

    const handleScreenshotAttempt = () => {
      const effects = [
        // createRedWarningOverlay,
        // createBlackScreenOverlay,
        createBlurOverlay,
      ];

      // Chọn ngẫu nhiên một hiệu ứng
      const randomEffect = effects[Math.floor(Math.random() * effects.length)];
      randomEffect();
    };

    const createBlurOverlay = () => {
      // Làm mờ toàn bộ body
      document.body.style.filter = "blur(20px)";
      document.body.style.transition = "filter 0.1s";

      // Ngăn scroll
      document.body.style.overflow = "hidden";

      const overlay = document.createElement("div");
      overlay.id = "screenshot-blocker";
      overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(255, 255, 255, 0.9);
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin: 0;
    padding: 0;
  `;

      overlay.innerHTML = `
    <div style="color: #ff0000; font-size: 50px; font-weight: bold; text-align: center;">
      🔒 NỘI DUNG BỊ CHE 🔒
    </div>
  `;

      document.body.appendChild(overlay);

      setTimeout(() => {
        document.body.style.filter = "none";
        document.body.style.overflow = ""; // Khôi phục scroll
        if (document.getElementById("screenshot-blocker")) {
          document.body.removeChild(overlay);
        }
      }, 3000);
    };

    const addOverlayStyles = () => {
      if (!document.getElementById("overlay-styles")) {
        const style = document.createElement("style");
        style.id = "overlay-styles";
        style.textContent = `
      @keyframes flashRed {
        0% { background: #ff0000; }
        50% { background: #990000; }
        100% { background: #ff0000; }
      }

      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }

      @keyframes staticNoise {
        0% { opacity: 1; }
        25% { opacity: 0.8; }
        50% { opacity: 0.6; }
        75% { opacity: 0.9; }
        100% { opacity: 1; }
      }
    `;
        document.head.appendChild(style);
      }
    };

    const removeOverlayAfterDelay = (overlay, delay = 3000) => {
      setTimeout(() => {
        if (document.getElementById("screenshot-blocker")) {
          document.body.removeChild(overlay);
        }
      }, delay);
    };

    document.addEventListener("keydown", handleKeyEvent);
    document.addEventListener("keyup", handleKeyEvent);

    return () => {
      document.removeEventListener("keydown", handleKeyEvent);
      document.removeEventListener("keyup", handleKeyEvent);

      // Cleanup overlay nếu component unmount
      const overlay = document.getElementById("screenshot-blocker");
      if (overlay) {
        document.body.removeChild(overlay);
      }
    };
  }, [id, current]);

  const handleSelect = (value, sub, q) => {
    if (!q || !q.question) {
      console.error("Invalid question object:", q);
      return;
    }

    // Ngăn chặn scroll khi đang xử lý answer
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    const questionNumber = q.question;

    switch (q.type) {
      case "TN":
        updateAnswer(questionNumber, value);
        break;
      case "MA":
        const currentAnswers = answers[questionNumber] || [];
        const newAnswers = currentAnswers.includes(value)
          ? currentAnswers.filter((a) => a !== value)
          : [...currentAnswers, value];
        updateAnswer(questionNumber, newAnswers);
        break;
      case "DS":
        const currentDSAnswer = answers[questionNumber] || {};
        updateAnswer(questionNumber, {
          ...currentDSAnswer,
          ...value,
        });
        break;
      case "TLN":
        // Parse number if possible, otherwise keep as string
        let numericValue = value;
        if (typeof value === "string" && value.includes(",")) {
          numericValue = numericValue.replace(",", ".");
        }

        if (isNumeric(numericValue)) {
          numericValue = parseFloat(numericValue);
          updateAnswer(questionNumber, numericValue);
        } else {
          updateAnswer(questionNumber, value);
        }

        break;
      case "TLN_M":
        {
          // Parse number if possible, otherwise keep as string
          let numericValue = value;

          if (typeof value === "string" && value.includes(",")) {
            numericValue = numericValue.replace(",", ".");
          }

          const currentTLNMAnswer = answers[questionNumber] || {};
          if (isNumeric(numericValue)) {
            numericValue = parseFloat(numericValue);
            updateAnswer(questionNumber, {
              ...currentTLNMAnswer,
              [sub.key]: numericValue,
            });
          } else {
            updateAnswer(questionNumber, {
              ...currentTLNMAnswer,
              [sub.key]: value,
            });
          }
        }
        break;
      default:
        break;
    }

    // Khôi phục scroll position sau khi xử lý
    setTimeout(() => {
      window.scrollTo(0, currentScrollTop);
    }, 0);
  };

  // Hàm scroll an toàn không gây vệt trắng
  const scrollToQuestion = (questionIndex) => {
    // Lấy question để check số thứ tự
    const targetQuestion = questions[questionIndex];
    if (!targetQuestion) return;

    const questionNumber = parseInt(
      targetQuestion.question.match(/\d+/)?.[0] || "0"
    );
    const currentQuestionMQ = questionsMQ.find((e) =>
      e?.range.includes(questionNumber)
    );

    if (currentQuestionMQ) {
      // Scroll cho MQ questions
      setTimeout(() => {
        const element = document.getElementById(`question-${questionIndex}`);
        if (element) {
          const container = element.closest(".overflow-y-auto");

          if (container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            const isAbove = elementRect.top < containerRect.top;
            const isBelow = elementRect.bottom > containerRect.bottom;

            if (isAbove || isBelow) {
              const scrollTopDelta =
                element.offsetTop -
                container.offsetTop -
                container.clientHeight / 3;

              container.scrollTo({
                top: scrollTopDelta,
                behavior: "smooth", // Vẫn giữ mượt nhưng không phụ thuộc scrollIntoView
              });
            }
          }
        }
      }, 100);
    }
    // Non-MQ questions không cần scroll vì chúng fullscreen
  };

  // Hàm điều hướng cho nút Previous/Next - Navigation theo range hoặc từng câu
  const handleNav = (idx, isNext) => {
    const currentQuestion = questions[current];
    if (!currentQuestion) return;

    const currentQuestionNumber = parseInt(
      currentQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Tìm tất cả các MQ groups và sắp xếp theo thứ tự
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Kiểm tra câu hỏi hiện tại có thuộc MQ group không
    const currentMQGroup = sortedMQGroups.find((e) =>
      e?.range.includes(currentQuestionNumber)
    );

    if (isNext) {
      let targetQuestionNumber;

      if (currentMQGroup) {
        // Câu hiện tại thuộc MQ group - chuyển sang range tiếp theo
        const currentMQIndex = sortedMQGroups.findIndex(
          (group) => group === currentMQGroup
        );
        const nextMQGroup = sortedMQGroups[currentMQIndex + 1];

        if (nextMQGroup) {
          // Có MQ group tiếp theo - chuyển tới câu đầu tiên của range đó
          targetQuestionNumber = nextMQGroup.range[0];
        } else {
          // Không có MQ group tiếp theo - tìm câu hỏi đơn lẻ tiếp theo
          const lastQuestionInCurrentRange = Math.max(...currentMQGroup.range);
          const allQuestionNumbers = questions
            .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
            .sort((a, b) => a - b);

          targetQuestionNumber = allQuestionNumbers.find(
            (num) =>
              num > lastQuestionInCurrentRange &&
              !sortedMQGroups.some((group) => group.range.includes(num))
          );
        }
      } else {
        // Câu hiện tại không thuộc MQ group - chuyển từng câu đơn lẻ
        const allQuestionNumbers = questions
          .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
          .sort((a, b) => a - b);

        // Tìm câu đơn lẻ tiếp theo trước
        const nextSingleQuestion = allQuestionNumbers.find(
          (num) =>
            num > currentQuestionNumber &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );

        if (nextSingleQuestion) {
          // Có câu đơn lẻ tiếp theo - chuyển tới câu đó
          targetQuestionNumber = nextSingleQuestion;
        } else {
          // Không có câu đơn lẻ tiếp theo - tìm MQ group tiếp theo
          const nextMQGroup = sortedMQGroups.find(
            (group) => group.range[0] > currentQuestionNumber
          );
          if (nextMQGroup) {
            targetQuestionNumber = nextMQGroup.range[0];
          }
        }
      }

      // Tìm index của target question và chuyển tới
      if (targetQuestionNumber) {
        const targetIndex = questions.findIndex(
          (q) =>
            parseInt(q.question.match(/\d+/)?.[0] || "0") ===
            targetQuestionNumber
        );

        if (targetIndex !== -1 && targetIndex < questions.length) {
          setCurrent(targetIndex);

          const targetMQGroup = sortedMQGroups.find((e) =>
            e?.range.includes(targetQuestionNumber)
          );

          if (targetMQGroup) {
            setCurrentMQ(targetQuestionNumber);
          } else {
            setCurrentMQ(0);
          }

          scrollToQuestion(targetIndex);
        }
      }
    } else {
      // Back: logic tương tự nhưng ngược lại
      let targetQuestionNumber;

      if (currentMQGroup) {
        // Câu hiện tại thuộc MQ group - chuyển sang range trước đó
        const currentMQIndex = sortedMQGroups.findIndex(
          (group) => group === currentMQGroup
        );
        const prevMQGroup = sortedMQGroups[currentMQIndex - 1];

        if (prevMQGroup) {
          // Có MQ group trước đó - chuyển tới câu đầu tiên của range đó
          targetQuestionNumber = prevMQGroup.range[0];
        } else {
          // Không có MQ group trước đó - tìm câu hỏi đơn lẻ trước đó
          const firstQuestionInCurrentRange = Math.min(...currentMQGroup.range);
          const allQuestionNumbers = questions
            .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
            .sort((a, b) => b - a); // Sort descending để tìm câu gần nhất

          targetQuestionNumber = allQuestionNumbers.find(
            (num) =>
              num < firstQuestionInCurrentRange &&
              !sortedMQGroups.some((group) => group.range.includes(num))
          );
        }
      } else {
        // Câu hiện tại không thuộc MQ group - chuyển từng câu đơn lẻ
        const allQuestionNumbers = questions
          .map((q) => parseInt(q.question.match(/\d+/)?.[0] || "0"))
          .sort((a, b) => b - a); // Sort descending

        // Tìm câu đơn lẻ trước đó trước
        const prevSingleQuestion = allQuestionNumbers.find(
          (num) =>
            num < currentQuestionNumber &&
            !sortedMQGroups.some((group) => group.range.includes(num))
        );

        if (prevSingleQuestion) {
          // Có câu đơn lẻ trước đó - chuyển tới câu đó
          targetQuestionNumber = prevSingleQuestion;
        } else {
          // Không có câu đơn lẻ trước đó - tìm MQ group trước đó
          const prevMQGroup = [...sortedMQGroups]
            .reverse()
            .find((group) => group.range[0] < currentQuestionNumber);
          if (prevMQGroup) {
            targetQuestionNumber = prevMQGroup.range[0];
          }
        }
      }

      // Tìm index của target question và chuyển tới
      if (targetQuestionNumber) {
        const targetIndex = questions.findIndex(
          (q) =>
            parseInt(q.question.match(/\d+/)?.[0] || "0") ===
            targetQuestionNumber
        );

        if (targetIndex !== -1 && targetIndex >= 0) {
          setCurrent(targetIndex);

          const targetMQGroup = sortedMQGroups.find((e) =>
            e?.range.includes(targetQuestionNumber)
          );

          if (targetMQGroup) {
            setCurrentMQ(targetQuestionNumber);
          } else {
            setCurrentMQ(0);
          }

          scrollToQuestion(targetIndex);
        }
      }
    }
  };

  // Hàm điều hướng từ Info Panel
  const handleNavFromPanel = (idx) => {
    // Lấy câu hỏi tại index được click
    const clickedQuestion = questions[idx];
    if (!clickedQuestion) return;

    // Lấy số thứ tự câu hỏi
    const questionNumber = parseInt(
      clickedQuestion.question.match(/\d+/)?.[0] || "0"
    );

    // Tìm tất cả các MQ groups
    const sortedMQGroups = questionsMQ.sort((a, b) => a.range[0] - b.range[0]);

    // Kiểm tra xem câu hỏi này có thuộc MQ group nào không
    const mqGroup = sortedMQGroups.find((e) =>
      e?.range.includes(questionNumber)
    );

    if (mqGroup) {
      // Nếu thuộc MQ group, set currentMQ về đúng câu hỏi được click
      setCurrentMQ(questionNumber);
      setCurrent(idx); // Set current về đúng index được click
      scrollToQuestion(idx);
    } else {
      // Nếu không thuộc MQ group, đây là câu hỏi bình thường
      setCurrent(idx);
      setCurrentMQ(0); // Reset currentMQ để đảm bảo không hiển thị UI MQ
      scrollToQuestion(idx);
    }
  };

  const handleSubmitClick = () => {
    // Nếu hết giờ, không hiện SubmitConfirmationModal
    if (isTimeUp) {
      return;
    }
    setShowSubmitModal(true);
  };

  // Hàm xử lý khi hết giờ (được gọi từ Countdown component)
  const handleTimeUp = () => {
    setIsTimeUp(true);
    setShowTimeUpModal(true);
    setShowSubmitModal(false); // Đóng modal submit thường nếu đang mở
  };

  // Hàm tự động submit khi countdown trong TimeUpModal kết thúc
  const handleAutoSubmit = async () => {
    setShowTimeUpModal(false);
    setIsSubmitting(true);
    try {
      await handleSubmit();
    } catch (error) {
      console.error("Auto submit failed:", error);
      // Nếu auto submit thất bại, cho phép user thử submit thủ công
      setIsTimeUp(false);
      setShowSubmitModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await handleSubmit();
      // Chỉ đóng modal khi submit thành công
      setShowSubmitModal(false);
    } catch (error) {
      // Nếu có lỗi, không đóng modal để user có thể thử lại
      console.error("Submit failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // TODO: Implement exam submission
    try {
      const answersForSubmit = {};
      let savedAnswers = getAnswersFromStorage(id);
      Object.keys(savedAnswers || {}).map((key) => {
        const question = questions.find((q) => q.question === key);
        answersForSubmit[key] = {
          answer: savedAnswers[key],
          type: question?.type,
        };
      });
      // Calculate the time used
      const res = await postTest(id, {
        userAnswers: answersForSubmit || {},
        examId: id,
        access: examData.access,
      });
      toast.success(res.message);

      // Clear answers using hook
      clearAnswers();

      const timeStorageKey = getTimeStorageKey(id);
      const markedQuestionsStorageKey = getMarkedQuestionsStorageKey(id);
      localStorage.removeItem(timeStorageKey);
      localStorage.removeItem(markedQuestionsStorageKey);
      localStorage.removeItem(`exam_time_${id}`);
      localStorage.removeItem(`current_time_${id}_${current}`);
      localStorage.removeItem(`exam_current_${id}`);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key.startsWith("current_time_")) {
          localStorage.removeItem(key);
        }
      }

      // Tự động thoát toàn màn hình khi nộp bài thành công
      exitFullscreen();

      navigate(`/exam-result/${id}`);
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      toast.error(message);
    }
  };

  // Hàm kiểm tra xem câu hỏi đã được trả lời hay chưa
  const isQuestionAnswered = (question) => {
    if (!question || !answers[question.question]) {
      return false;
    }

    const answer = answers[question.question];
    let isAnswered = false;

    switch (question.type) {
      case "TN":
        // Trắc nghiệm: đã chọn một đáp án (A, B, C, D)
        isAnswered = answer !== null && answer !== undefined;
        break;

      case "MA":
        // Nhiều lựa chọn: đã chọn ít nhất một đáp án
        isAnswered = Array.isArray(answer) && answer.length > 0;
        break;

      case "DS":
        // Đúng/Sai: đã chọn ít nhất một mệnh đề
        isAnswered =
          typeof answer === "object" &&
          Object.keys(answer).length > 0 &&
          Object.values(answer).some((val) => val === "D" || val === "S");
        break;

      case "TLN":
        // Trả lời ngắn: đã nhập câu trả lời (không rỗng)
        isAnswered = answer !== null && answer !== undefined && answer !== "";
        break;

      case "TLN_M":
        // Trả lời ngắn nhiều mệnh đề: đã nhập ít nhất một câu trả lời
        if (typeof answer !== "object") {
          isAnswered = false;
        } else {
          isAnswered = Object.values(answer).some((val) => {
            // Kiểm tra giá trị hợp lệ, bỏ qua các giá trị rỗng
            return val !== null && val !== undefined && val !== "";
          });
        }
        break;

      case "KT":
        // Kéo thả: đã kéo ít nhất một item vào slot
        if (typeof answer !== "object") {
          isAnswered = false;
        } else {
          isAnswered = Object.values(answer).some(
            (val) => val !== null && val !== undefined
          );
        }
        break;

      default:
        isAnswered = false;
    }

    return isAnswered;
  };

  // Số câu hỏi đã trả lời
  const doneCount = questions.filter((q) => isQuestionAnswered(q)).length;

  const onDragEnd = (question) => (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;

    const questionNumber = question?.question;

    // Nếu vị trí nguồn và đích giống nhau, không làm gì cả
    if (destination.droppableId === source.droppableId) {
      return;
    }

    updateAnswer((prev) => {
      const currentAnswers = prev[questionNumber] || {};
      const updatedAnswers = { ...currentAnswers };

      // Nếu kéo từ slot này sang slot khác
      if (
        source.droppableId.startsWith("slot") &&
        destination.droppableId.startsWith("slot")
      ) {
        // Xóa item khỏi slot cũ
        updatedAnswers[source.droppableId] = null;
        // Thêm item vào slot mới
        updatedAnswers[destination.droppableId] = draggableId;
      }
      // Nếu kéo từ hàng items xuống slot
      else if (
        source.droppableId === "items" &&
        destination.droppableId.startsWith("slot")
      ) {
        updatedAnswers[destination.droppableId] = draggableId;
      }
      // Nếu kéo từ slot lên hàng items
      else if (
        source.droppableId.startsWith("slot") &&
        destination.droppableId === "items"
      ) {
        updatedAnswers[source.droppableId] = null;
      }

      const newState = {
        ...prev,
        [questionNumber]: updatedAnswers,
      };

      // Lưu ngay đáp án sau khi kéo thả
      saveAnswersToStorage(id, newState);

      return newState;
    });
  };

  // Hàm để đánh dấu hoặc bỏ đánh dấu câu hỏi
  const toggleMarkQuestion = (idx) => {
    setMarkedQuestions((prev) => {
      let newMarkedQuestions;
      if (prev.includes(idx)) {
        // Nếu câu hỏi đã được đánh dấu, bỏ đánh dấu
        newMarkedQuestions = prev.filter((q) => q !== idx);
        // toast.info(`Đã bỏ đánh dấu câu ${idx + 1}`, {
        //   position: "top-right",
        //   autoClose: 2000,
        //   hideProgressBar: true,
        //   icon: "🔖",
        // });
      } else {
        // Nếu câu hỏi chưa được đánh dấu, thêm vào danh sách
        newMarkedQuestions = [...prev, idx];
        // toast.success(`Đã đánh dấu câu ${idx + 1} để làm sau`, {
        //   position: "top-right",
        //   autoClose: 2000,
        //   hideProgressBar: true,
        //   icon: "🔖",
        // });
      }

      // Lưu danh sách đánh dấu vào localStorage
      saveMarkedQuestionsToStorage(id, newMarkedQuestions);

      return newMarkedQuestions;
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="h-screen flex flex-col bg-[#f8f9fa] exam-no-select">
      {/* Header */}
      <ExamHeader examData={examData} />

      {/* Main Content */}
      <div className="flex-1 flex gap-0 max-w-full mx-auto w-full relative overflow-hidden h-[calc(100vh-64px)]">
        {/* Main Question Area */}
        <div
          className="flex flex-col flex-1 px-2 py-3 overflow-hidden md:px-3 md:py-4"
          style={{ scrollBehavior: "auto" }}
        >
          <ExamQuestion
            id={id}
            questions={questions}
            questionsMQ={questionsMQ}
            current={current}
            currentMQ={currentMQ}
            answers={answers}
            markedQuestions={markedQuestions}
            toggleMarkQuestion={toggleMarkQuestion}
            handleSelect={handleSelect}
            onDragEnd={onDragEnd}
            isQuestionAnswered={isQuestionAnswered}
            onAnswerChange={updateAnswer}
          />

          {/* Navigation Footer */}
          <ExamNavigation
            currentMQ={currentMQ}
            questionsMQ={questionsMQ}
            current={current}
            isDisabledBack={isDisabledBack}
            isDisabledNext={isDisabledNext}
            handleNav={handleNav}
            examData={examData}
            handleSubmit={handleSubmitClick}
            handleTimeUp={handleTimeUp}
            setShowInfoPanel={setShowInfoPanel}
            showInfoPanel={showInfoPanel}
            isCurrentQuestionMQ={isCurrentQuestionMQ}
          />

          {/* Slide-out Info Panel */}
          <ExamInfoPanel
            showInfoPanel={showInfoPanel}
            setShowInfoPanel={setShowInfoPanel}
            examData={examData}
            handleSubmit={handleSubmitClick}
            handleTimeUp={handleTimeUp}
            questions={questions}
            current={current}
            currentMQ={currentMQ}
            questionsMQ={questionsMQ}
            isQuestionAnswered={isQuestionAnswered}
            markedQuestions={markedQuestions}
            handleNavFromPanel={handleNavFromPanel}
            doneCount={doneCount}
            isCurrentQuestionMQ={isCurrentQuestionMQ}
          />
        </div>

        {/* Fixed Info Panel - chỉ hiện khi không phải câu hỏi MQ */}
        <ExamFixedInfoPanel
          currentMQ={currentMQ}
          questionsMQ={questionsMQ}
          examData={examData}
          handleSubmit={handleSubmitClick}
          handleTimeUp={handleTimeUp}
          questions={questions}
          current={current}
          isQuestionAnswered={isQuestionAnswered}
          markedQuestions={markedQuestions}
          handleNavFromPanel={handleNavFromPanel}
          doneCount={doneCount}
          isCurrentQuestionMQ={isCurrentQuestionMQ}
        />
      </div>

      {/* Modals */}
      <ExamModals
        showFullscreenModal={showFullscreenModal}
        shouldPromptFullscreen={shouldPromptFullscreen}
        setShouldPromptFullscreen={setShouldPromptFullscreen}
        handleBackToFullscreen={handleBackToFullscreen}
      />

      {/* Submit Confirmation Modal */}
      <SubmitConfirmationModal
        isOpen={showSubmitModal}
        onClose={() => !isSubmitting && setShowSubmitModal(false)}
        onConfirm={handleConfirmSubmit}
        totalQuestions={questions.length}
        answeredQuestions={doneCount}
        isSubmitting={isSubmitting}
      />

      {/* Time Up Modal */}
      <TimeUpModal isOpen={showTimeUpModal} onAutoSubmit={handleAutoSubmit} />
    </div>
  );
};

export default ExamTestPage;
